// authResume — pure decision helper for BOTH auth paths (mount cold-start and
// the onAuthStateChange resume/token-refresh).
//
// Kept import-free on purpose so it is unit-testable in node without dragging
// in src/lib/supabase.js (which calls createClient at module load). AuthContext
// imports resolvePlayerState and uses it in both the mount getSession().then
// branch and the onAuthStateChange handler.
//
// The bug this exists to prevent (ops issue #21): a backgrounded tab/app
// returns and AuthContext re-fetches the player row from the get-my-player
// Netlify function. If that fetch transiently fails (a cold-started function,
// a brief network drop, or a 401 during token rotation) while the session is
// still valid, unconditionally clearing the player sends a logged-in user to
// SignupPage ("Finish your profile").
//
// On a phone this happens on a COLD START too, not just a live TOKEN_REFRESHED:
// Android's WebView background-killer frequently kills a backgrounded PWA, so
// returning runs the MOUNT path, not a resume event. The fix therefore has to
// cover both paths, which is why the decision lives in one shared helper.
//
// The critical distinction is transient-failure vs authoritative-absent:
//   - 'found'  → the server returned a real player row.
//   - 'absent' → the server returned HTTP 200 with data:null. This is
//                AUTHORITATIVE: the auth user genuinely has no player row
//                (a brand-new OTP user who hasn't completed signup, or a
//                deleted row). Clearing + routing to SignupPage is correct.
//   - 'error'  → a network throw or a non-OK HTTP status (401/5xx/timeout).
//                This proves NOTHING about whether a row exists. Never clear
//                a logged-in user on this; keep the last-good player
//                (stale-while-revalidate).

/**
 * @typedef {{ status: 'found' | 'absent' | 'error', player: (object|null) }} FetchOutcome
 */

/**
 * Decide the player value + cache action after an auth event / mount fetch.
 *
 * cache action semantics (the caller applies these against the players cache):
 *   'write' → persist the freshly fetched player.
 *   'clear' → drop the cache (real sign-out, or an authoritative no-row).
 *   'keep'  → leave the cache untouched (transient failure — do not destroy
 *             the last-good value on a network blip).
 *
 * @param {object}       args
 * @param {object|null}  args.session        Current session (null === signed out).
 * @param {FetchOutcome} args.fetchOutcome   The result of the player fetch.
 * @param {object|null}  args.priorPlayer    The player currently in state.
 * @returns {{ player: (object|null), cache: 'write'|'clear'|'keep', confirmedAbsent: boolean }}
 */
export function resolvePlayerState({ session, fetchOutcome, priorPlayer }) {
  const status = fetchOutcome?.status || 'error'
  const fetched = fetchOutcome?.player || null

  // No session = signed out (or never signed in). Always clear.
  if (!session) return { player: null, cache: 'clear', confirmedAbsent: false }

  // Session present + a real player fetched: use the fresh row.
  if (status === 'found' && fetched) {
    return { player: fetched, cache: 'write', confirmedAbsent: false }
  }

  // Session present but the server AUTHORITATIVELY reports no row (200 + null).
  // Brand-new OTP user (no prior) or a genuinely deleted row. Clear and let the
  // App gate route to SignupPage — this is the legitimate "no profile" case.
  if (status === 'absent') {
    return { player: null, cache: 'clear', confirmedAbsent: true }
  }

  // status === 'error': transient (cold-started function, network drop, 401 in
  // token rotation, timeout). We do NOT know whether a row exists, so keep the
  // last-good player and never destroy the cache. confirmedAbsent stays false
  // so the App gate shows a loading/splash state instead of falsely demanding
  // re-signup.
  return { player: priorPlayer ?? null, cache: 'keep', confirmedAbsent: false }
}

// --- App-gate decision helpers (pure, so App.jsx's gate is unit-testable) ---
//
// F-050: the boot splash must appear ONLY on a genuine first cold start with
// nothing to paint. The single most important rule: if a player is already on
// screen (painted from the instant-paint cache or held from a prior fetch), a
// background re-fetch (playerLoading) must NEVER flash the splash. Keying the
// gate off `player` presence — not merely `playerLoading`/`loading` — is what
// prevents the real-screen → splash → real-screen flash on background→resume.

/**
 * Should the App show the boot splash?
 *
 * TRUE when either:
 *   (a) auth is still resolving (initial cold-start `loading` OR a resume
 *       re-fetch `playerLoading`) AND there is NO player available to paint —
 *       a genuine first cold start with nothing to show; or
 *   (b) the initial cold-start fetch has NOT settled AND the session is not
 *       yet resolved (`loading && !session`) — even when a stale instant-paint
 *       `player` is already on screen. In that window `isAuthed`
 *       (session && isSignedUp) is false because `session` is still null, so
 *       falling through the gate would route to PlayerLoginPage / SignupPage
 *       and flash a login/signup screen at a signed-in user on resume. This
 *       is the exact hole the 24h instant-paint TTL widened: with the old
 *       ~15min TTL a multi-hour-old resume had an expired cache (no painted
 *       player) so clause (a) held the splash; with the wider TTL a painted-
 *       but-session-unresolved render used to fall through to the login page.
 *       Holding the neutral splash until `getSession()` resolves closes it.
 *       Scoped to `loading` (the one-time mount resolve), NOT `playerLoading`
 *       (the warm background→foreground resume re-fetch, where `session` is
 *       already non-null), so a resume with a painted player still never
 *       flashes the splash (F-050). Clears the instant `getSession()` resolves:
 *       `session` becomes non-null for a signed-in user, or `loading` flips
 *       false for a truly logged-out one (who then routes to login as intended).
 *
 * A cached / last-good `player` present with a resolved session => keep
 * rendering the app through the background re-fetch (stale-while-revalidate);
 * never flash splash.
 *
 * @param {{ loading: boolean, playerLoading: boolean, session: (object|null), player: (object|null) }} args
 * @returns {boolean}
 */
export function shouldShowBootSplash({ loading, playerLoading, session, player }) {
  if ((loading || playerLoading) && !player) return true
  if (loading && !session) return true
  return false
}

/**
 * Should the App render SignupPage ("Finish your profile")?
 *
 * #239 preservation: demand signup ONLY when a live session is NOT signed up
 * AND we are SURE about the profile — either a real (incomplete) player row is
 * in hand, OR the server authoritatively confirmed no row exists
 * (playerMissingConfirmed). A transient / still-in-flight fetch leaves BOTH
 * `player` null and `playerMissingConfirmed` false, so this returns false and
 * the App holds on the splash instead of falsely demanding re-signup. The
 * "fetch has settled, not in-flight" rule is enforced upstream: AuthContext
 * only sets playerMissingConfirmed true after an 'absent' outcome resolves.
 *
 * @param {{ session: (object|null), isSignedUp: boolean, player: (object|null), playerMissingConfirmed: boolean }} args
 * @returns {boolean}
 */
export function shouldShowSignup({ session, isSignedUp, player, playerMissingConfirmed }) {
  if (!(session && !isSignedUp)) return false
  return !!(player || playerMissingConfirmed)
}

/**
 * Should the App show the restore-account gate instead of proceeding into the
 * app? (Apple 5.1.1(v) in-app account deletion — the sign-in-side of the soft
 * delete.)
 *
 * TRUE when a signed-in user's own player row carries a non-null
 * deletion_requested_at — i.e. they previously tapped "Delete account" and are
 * now signing back in. The App gate must BLOCK entry and show the restore modal
 * (Yes → restore-account clears the flag and continues; No → sign out) rather
 * than dropping a pending-deletion user straight into the app.
 *
 * get-my-player does `SELECT *`, so deletion_requested_at is already on the
 * player object with no extra fetch. A missing session or player, or a null
 * flag (active account), returns false and the app proceeds normally.
 *
 * @param {{ session: (object|null), player: (object|null) }} args
 * @returns {boolean}
 */
export function shouldShowRestoreGate({ session, player }) {
  return !!(session && player && player.deletion_requested_at)
}
